<template>
    <div class="content-wrapper target-time">
        <table :class="['target-time-table', 'no-print', navCollapsed ? 'collapsed' : 'not-collapsed']">
            <thead>
                <tr v-show="isLoaded">
                    <th>{{ $t('settings.targetTimeTab.callType') }}</th>
                    <th>{{ $t('settings.targetTimeTab.targetTimeEnable') }}</th>
                    <th>{{ $t('settings.targetTimeTab.targetTime') }} (mm:ss)</th>
                    <th></th>
                    <th>{{ $t('settings.targetTimeTab.delayedTime') }} (mm:ss)</th>
                    <th>{{ $t('settings.targetTimeTab.targetTimeTill') }}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(call, name) in calls" :key="call.category_id">
                    <td :title="$t('reporting.callTypes.' + name)">
                        <i :class="'icon-' + iconName(name)"></i>{{ $t('reporting.callTypes.' + name) }}
                    </td>
                    <td>
                        <div class="target-time-enabled-wrapper chkbox">
                            <input
                                :id="'target-time-enabled-' + name"
                                type="checkbox"
                                :class="['pointer', { checked: call.time_target_enabled }]"
                            />
                            <label
                                for="'target-time-enabled-' + name"
                                @click="call.time_target_enabled = !call.time_target_enabled"
                            ></label>
                        </div>
                    </td>
                    <td>
                        <div class="target-time-wrapper">
                            <span v-if="$v.validValues.$each[name].target.$invalid" class="tooltiptext">{{
                                errorDisplay($v.validValues.$each[name].target)
                            }}</span>
                            <input
                                v-model="validValues[name].target"
                                :class="{ tooltiperror: $v.validValues.$each[name].target.$invalid }"
                                :disabled="!call.time_target_enabled"
                                @input="$v.validValues.$each[name].target.$touch()"
                            />
                        </div>
                    </td>
                    <td>
                        <Slider
                            :id="call.category_id"
                            v-model="sliderValues[name].value"
                            :disabled="!call.time_target_enabled"
                            :max="sliderValues[name].max"
                        />
                    </td>
                    <td>
                        <div class="target-time-wrapper">
                            <span v-if="$v.validValues.$each[name].delayed.$invalid" class="tooltiptext">{{
                                errorDisplay($v.validValues.$each[name].delayed)
                            }}</span>
                            <input
                                v-model="validValues[name].delayed"
                                :disabled="!call.time_target_enabled"
                                :class="{ tooltiperror: $v.validValues.$each[name].delayed.$invalid }"
                                @input="$v.validValues.$each[name].delayed.$touch()"
                            />
                        </div>
                    </td>
                    <td>
                        <SelectBox
                            v-model="call.time_till"
                            :options="selectBoxTillTypes(call.category_id)"
                            :is-disabled="!call.time_target_enabled"
                            :search="false"
                        />
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script type="text/babel">
import { mapState, mapActions } from 'vuex';
import cloneDeep from 'lodash-es/cloneDeep';
import forEach from 'lodash-es/forEach';
import mappingState from '../common/mappingState';
import { secondsToMMSS, ifIsNotCanceled } from '../common/helpers';
import SelectBox from '../common/SelectBox.vue';
import Slider from '../common/Slider.vue';
import { correctMMSS, maxValueNotExceeded, errorDisplay } from '../../validators';
import { resourceTargetTime } from '../../api/resources';
import { getCancelToken } from '../../api';
import { SUCCESS, ROUTE_NAMES } from '../../constants';

export default {
    name: 'TargetTime',

    components: {
        SelectBox,
        Slider,
    },

    mixins: [mappingState],

    data() {
        return {
            calls: [],
            callsCopy: [],
            sliderValues: {
                acknowledgement: { max: 1800, value: [] },
                assistance: { max: 1200, value: [] },
                critical: { max: 180, value: [] },
                observe: { max: 1200, value: [] },
                medical_medium: { max: 300, value: [] },
                errors: { max: 1800, value: [] },
                service: { max: 3600, value: [] },
                technical: { max: 3600, value: [] },
                security: { max: 1800, value: [] },
                medical_low: { max: 300, value: [] },
                medical_high: { max: 180, value: [] },
            },
            validValues: {
                acknowledgement: { max: 1800, target: '', delayed: '' },
                assistance: { max: 1200, target: '', delayed: '' },
                critical: { max: 180, target: '', delayed: '' },
                observe: { max: 1200, target: '', delayed: '' },
                medical_medium: { max: 300, target: '', delayed: '' },
                errors: { max: 1800, target: '', delayed: '' },
                service: { max: 3600, target: '', delayed: '' },
                technical: { max: 3600, target: '', delayed: '' },
                security: { max: 1800, target: '', delayed: '' },
                medical_low: { max: 300, target: '', delayed: '' },
                medical_high: { max: 180, target: '', delayed: '' },
            },
            tillTypes: [
                {
                    name: this.$t('settings.targetTimeTab.tillTypes.callAccepted'),
                    value: 1,
                    hiddenInCategories: [3],
                },
                {
                    name: this.$t('settings.targetTimeTab.tillTypes.nursePresence'),
                    value: 2,
                    hiddenInCategories: [4, 5, 6, 7, 8, 9, 10, 11],
                },
                {
                    name: this.$t('settings.targetTimeTab.tillTypes.callEnd'),
                    value: 3,
                    hiddenInCategories: [],
                },
            ],
            cancelToken: getCancelToken(),
            isLoaded: false,
        };
    },

    computed: {
        ...mapState(['isLoading', 'unsavedChanges', 'navCollapsed']),
    },

    validations() {
        return {
            validValues: {
                $each: {
                    target: {
                        correctMMSS: (val, name) => {
                            return correctMMSS(val, name.max === 3600);
                        },
                        maxValueNotExceeded: (val, name) => {
                            return maxValueNotExceeded(val, name.delayed, name.max);
                        },
                    },
                    delayed: {
                        correctMMSS: (val, name) => {
                            return correctMMSS(val, name.max === 3600);
                        },
                        maxValueNotExceeded: (val, name) => {
                            return maxValueNotExceeded(val, name.target, name.max);
                        },
                    },
                },
            },
        };
    },

    watch: {
        calls: {
            handler() {
                this.setUnsavedChanges(true);
                this.setValidValues(this.calls);
                this.setSliderValues(this.calls);
            },
            deep: true,
        },

        sliderValues: {
            handler() {
                this.setUnsavedChanges(true);
                this.updateCallsFromSlider();
            },
            deep: true,
        },

        validValues: {
            handler() {
                this.setUnsavedChanges(true);
                this.updateCallsFromInputs();
            },
            deep: true,
        },
    },

    created() {
        this.setSaveUndoButtons({
            undo: this.undo.bind(this),
            save: this.save.bind(this),
            saveDisabled: () => !this.unsavedChanges || this.$v.$invalid,
            wasChanged: () => !this.unsavedChanges,
        });
        this.loadTargetTime();
    },

    beforeDestroy() {
        this.cancelToken.cancel();
    },

    methods: {
        ...mapActions(['loadCallTypes']),
        ...mapActions({ setTargetTimeSettings: 'setTargetTimeSettings' }),
        secondsToMMSS,
        errorDisplay,

        loadTargetTime() {
            this.showLoading(true);
            return resourceTargetTime({ cancelToken: this.cancelToken.token })
                .then(response => {
                    this.calls = response.data;
                    this.callsCopy = cloneDeep(this.calls);
                    Promise.all([
                        this.setTargetTimeSettings(this.callsCopy),
                        this.setSliderValues(this.calls),
                        this.setValidValues(this.calls),
                    ]).then(() => {
                        this.setUnsavedChanges(false);
                        this.showLoading(false);
                        this.isLoaded = true;
                    });
                })
                .catch(
                    ifIsNotCanceled({
                        callback: response => {
                            this.calls = [];
                            this.isLoaded = true;
                            return response;
                        },
                    })
                );
        },

        /**
         * Returns only allowed 'till' events for particular call
         * @param id
         * @returns {Array.<T>|T[]}
         */
        selectBoxTillTypes(id) {
            return this.tillTypes.filter(t => !t.hiddenInCategories.includes(id));
        },

        undo() {
            this.calls = cloneDeep(this.callsCopy);
            this.setSliderValues(this.calls);
            this.setValidValues(this.calls);
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });
        },

        save() {
            this.showLoading(true);
            return resourceTargetTime({
                method: 'post',
                data: this.calls,
            })
                .then(() => {
                    if (this.$router.currentRoute.name === ROUTE_NAMES.SETTINGS__TARGET_TIME) {
                        this.popup({
                            type: SUCCESS,
                            header: this.$t('common.success'),
                            text: this.$t('common.saved'),
                        });
                        this.loadCallTypes(this.cancelToken.token);
                        this.callsCopy = cloneDeep(this.calls);
                        this.setUnsavedChanges(false);
                        this.showLoading(false);
                        this.setTargetTimeSettings(this.callsCopy);
                    }
                })
                .catch(ifIsNotCanceled());
        },

        iconName(name) {
            return this.$t('reporting.callTypes.' + name).toLowerCase();
        },

        /**
         * Sets calls value model for sliders. Updating full array is necessary for Slider component update triggering
         * @param calls
         */
        setSliderValues(calls) {
            forEach(calls, (key, name) => {
                if (this.sliderValues[name].value.length) {
                    if (
                        this.sliderValues[name].value[0] !== key.time_target ||
                        this.sliderValues[name].value[1] !== key.time_delayed
                    ) {
                        this.sliderValues[name].value = [key.time_target, key.time_delayed];
                    }
                } else {
                    this.sliderValues[name].value = [key.time_target, key.time_delayed];
                }
            });
        },

        /**
         * Sets temporary values for validation
         * @param calls
         */
        setValidValues(calls) {
            forEach(calls, (key, name) => {
                let t = secondsToMMSS(key.time_target);
                let d = secondsToMMSS(key.time_delayed - key.time_target);
                if (this.validValues[name].target !== t) {
                    this.validValues[name].target = t;
                }
                if (this.validValues[name].delayed !== d) {
                    this.validValues[name].delayed = d;
                }
            });
        },

        /**
         * Updates time_target and time_delayed on sliderValues change
         */
        updateCallsFromSlider() {
            forEach(this.calls, (key, name) => {
                let t = this.sliderValues[name].value[0];
                let d = this.sliderValues[name].value[1];
                if (key.time_target !== t) {
                    key.time_target = t;
                }
                if (key.time_delayed !== d) {
                    key.time_delayed = d;
                }
            });
        },

        /**
         * Updates time_target and time_delayed on input values change
         */
        updateCallsFromInputs() {
            forEach(this.calls, (key, name) => {
                if (!this.$v.validValues.$each[name].$invalid) {
                    let t = this.validValues[name].target.split(':');
                    let d = this.validValues[name].delayed.split(':');
                    let target = +t[0] * 60 + +t[1];
                    let delayed = +d[0] * 60 + +d[1] + target;

                    if (key.time_target !== target) {
                        key.time_target = target;
                    }
                    if (key.time_delayed !== delayed) {
                        key.time_delayed = delayed;
                    }
                }
            });
        },

        findTillType(value) {
            return this.tillTypes.find(type => type.value === value).name;
        },
    },
};
</script>
